import { gmImagesState, LANGUAGES } from '../state/gmImagesState.js';
import { toast } from '../toast.js';

export class GmImagesUI {
  constructor() {
    this.previewElements = {};
    this.fileInputElements = {};
    this.statusElements = {};
    this.initializeElements();
    LANGUAGES.forEach(lang => this.renderEmptySlot(lang));
  }

  initializeElements() {
    LANGUAGES.forEach(lang => {
      const langCap = this.capitalize(lang);
      this.previewElements[lang] = document.getElementById(`gm${langCap}Preview`);
      this.fileInputElements[lang] = document.getElementById(`gm${langCap}File`);
      this.statusElements[lang] = document.getElementById(`gm${langCap}Status`);
    });
  }

  capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  emptySlotHtml(lang) {
    const langCap = this.capitalize(lang);
    return `
      <label for="gm${langCap}File" class="gm-slot-upload">
        <svg class="w-8 h-8 text-purple-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
        </svg>
        <span class="text-xs text-purple-700 font-semibold">Upload image</span>
        <span class="text-[10px] text-purple-400 mt-1">2:3 portrait</span>
      </label>
    `;
  }

  filledSlotHtml(lang, imageUrl) {
    return `
      <button type="button" class="gm-slot-preview" data-expand-lang="${lang}" aria-label="Preview ${lang.toUpperCase()} image">
        <img src="${imageUrl}" alt="${lang.toUpperCase()} preview" />
      </button>
      <button type="button" class="gm-slot-clear" data-clear-lang="${lang}" title="Clear image to replace" aria-label="Clear ${lang.toUpperCase()} image">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    `;
  }

  renderEmptySlot(lang) {
    const slot = this.previewElements[lang];
    if (!slot) return;
    slot.classList.remove('has-image');
    slot.innerHTML = this.emptySlotHtml(lang);
  }

  setupFileHandlers(onFileSelected) {
    LANGUAGES.forEach(lang => {
      const fileInput = this.fileInputElements[lang];
      const slot = this.previewElements[lang];
      if (!fileInput || !slot) return;

      fileInput.addEventListener('change', (e) => {
        // Picking a replacement used to be blocked until the slot was
        // cleared, which meant deleting the stored image first — and losing
        // it outright if the new upload then failed. The upload path already
        // replaces safely: it deletes the old image only once the new file
        // is in hand (gmImagesController, uploadImages).
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
          toast.error('Please select an image file');
          fileInput.value = '';
          return;
        }

        if (file.size > 10 * 1024 * 1024) {
          toast.error('Image size must be less than 10MB');
          fileInput.value = '';
          return;
        }

        const reader = new FileReader();
        reader.onload = (ev) => {
          const previewUrl = ev.target.result;
          onFileSelected(lang, file, previewUrl);
          this.updatePreview(lang, previewUrl);
          this.updateLanguageStatuses();
          this.updateToolbarUI();
          toast.info(`${lang.toUpperCase()} image selected`);
        };
        reader.readAsDataURL(file);
      });

      // Expand / clear via event delegation on the slot
      slot.addEventListener('click', (e) => {
        const clearBtn = e.target.closest('[data-clear-lang]');
        if (clearBtn) {
          e.preventDefault();
          e.stopPropagation();
          window.clearGmImage?.(clearBtn.dataset.clearLang);
          return;
        }

        const expandBtn = e.target.closest('[data-expand-lang]');
        if (expandBtn) {
          e.preventDefault();
          this.expandImage(expandBtn.dataset.expandLang);
        }
      });
    });
  }

  hasAnyImage() {
    return LANGUAGES.some(lang => {
      const state = gmImagesState.getState(lang);
      return Boolean(state.file || state.preview || state.existingUrl);
    });
  }

  updateToolbarUI() {
    const updateGmBtn = document.getElementById('updateGmBtn');
    if (updateGmBtn) {
      updateGmBtn.disabled = false;
      updateGmBtn.removeAttribute('disabled');
    }

    const clearAllBtn = document.getElementById('gmClearAllBtn');
    if (clearAllBtn) {
      const enableClear = this.hasAnyImage();
      clearAllBtn.disabled = !enableClear;
      if (enableClear) clearAllBtn.removeAttribute('disabled');
    }
  }

  updateLanguageStatuses() {
    LANGUAGES.forEach(lang => {
      const statusEl = this.statusElements[lang];
      if (!statusEl) return;

      const state = gmImagesState.getState(lang);

      // The slot pill is a design-system badge. Overwriting className stripped
      // that off, so these were never badges in practice — set the tone instead.
      // The three labels are one scale: Uploaded / Not uploaded / Missing. They
      // used to be Saved / Ready / No image, where the word that sounded
      // finished ("Ready") named the state that was not.
      //
      // The pending test is the state's own, not `state.preview`: loading a
      // stored image and finishing an upload both set preview to the download
      // URL, so testing it directly reported every saved image as unsaved.
      let label, tone;
      if (gmImagesState.hasPendingUpload(lang)) {
        label = 'Not uploaded'; tone = 'warning';
      } else if (state.existingUrl) {
        label = 'Uploaded'; tone = 'positive';
      } else {
        // Missing reads red, not grey. A language with no image is the reason
        // the date is not ready, so it should look the same as every other
        // shortfall in the console rather than like an inert placeholder.
        label = 'Missing'; tone = 'negative';
      }
      statusEl.className = 'badge';
      statusEl.setAttribute('data-tone', tone);
      statusEl.textContent = label;

      // The whole label carries the state, not just the pill: the language name
      // and the frame around the image below it are what the eye lands on when
      // scanning four slots at once.
      const card = statusEl.closest('.gm-lang-card');
      if (card) card.setAttribute('data-tone', tone);
    });
  }

  updatePreview(lang, imageUrl) {
    const slot = this.previewElements[lang] || document.getElementById(`gm${this.capitalize(lang)}Preview`);
    this.previewElements[lang] = slot;
    if (!slot) {
      console.error(`[GM UI] Preview slot not found for ${lang}`);
      return;
    }

    if (imageUrl) {
      slot.classList.add('has-image');
      slot.innerHTML = this.filledSlotHtml(lang, imageUrl);
    } else {
      this.renderEmptySlot(lang);
    }
  }

  clearPreview(lang) {
    const langCap = this.capitalize(lang);
    const slot = this.previewElements[lang] || document.getElementById(`gm${langCap}Preview`);
    this.previewElements[lang] = slot;

    const fileInput = this.fileInputElements[lang] || document.getElementById(`gm${langCap}File`);
    this.fileInputElements[lang] = fileInput;
    if (fileInput) fileInput.value = '';

    this.renderEmptySlot(lang);
    this.updateLanguageStatuses();
    this.updateToolbarUI();
  }

  clearAllPreviews() {
    LANGUAGES.forEach(lang => this.clearPreview(lang));
  }

  restorePreviews(expectedDate = null) {
    const hasSavedState = gmImagesState.loadFromLocalStorage(expectedDate);
    if (hasSavedState) {
      LANGUAGES.forEach(lang => {
        const state = gmImagesState.getState(lang);
        if (state.preview) {
          this.updatePreview(lang, state.preview);
        } else {
          this.renderEmptySlot(lang);
        }
      });
    }
    this.updateLanguageStatuses();
    this.updateToolbarUI();
  }

  updateUploadButtonStates() {
    this.updateLanguageStatuses();
    this.updateToolbarUI();
  }

  resetForm() {
    LANGUAGES.forEach(lang => this.clearPreview(lang));
    const form = document.getElementById('gmImagesForm');
    if (form) form.reset();
  }

  expandImage(lang) {
    const state = gmImagesState.getState(lang);

    if (!state.preview) {
      toast.error('No image to preview');
      return;
    }

    const modal = document.getElementById('imagePreviewModal');
    const modalImage = document.getElementById('modalImage');
    const modalTitle = document.getElementById('modalImageTitle');
    const modalInfo = document.getElementById('modalImageInfo');

    if (!modal || !modalImage || !modalTitle || !modalInfo) return;

    modalImage.src = state.preview;
    modalTitle.textContent = `${lang.toUpperCase()} GM Image Preview`;

    if (state.file) {
      modalInfo.textContent = 'Chosen on this page — not saved to database yet';
    } else if (state.uploadedAt || state.existingUrl) {
      if (state.uploadedAt) {
        const date = new Date(state.uploadedAt);
        modalInfo.textContent = `Saved on ${date.toLocaleDateString()} at ${date.toLocaleTimeString()}`;
      } else {
        modalInfo.textContent = 'Saved in database';
      }
    } else {
      modalInfo.textContent = 'Chosen but not yet saved';
    }

    modal.classList.remove('hidden');
    modal.classList.add('flex');
  }

  closeImageModal() {
    const modal = document.getElementById('imagePreviewModal');
    if (modal) {
      modal.classList.add('hidden');
      modal.classList.remove('flex');
    }
  }

  setBulkButtonsLoading(isLoading, text = 'Saving...') {
    const updateGmBtn = document.getElementById('updateGmBtn');
    const clearAllBtn = document.getElementById('gmClearAllBtn');
    const buttons = [updateGmBtn, clearAllBtn].filter(Boolean);

    buttons.forEach(btn => {
      if (isLoading) {
        btn.dataset.originalHtml = btn.innerHTML;
        btn.disabled = true;
        btn.innerHTML = `<div class="spinner inline-block mr-2"></div>${text}`;
      } else if (btn.dataset.originalHtml) {
        btn.innerHTML = btn.dataset.originalHtml;
        delete btn.dataset.originalHtml;
        btn.disabled = false;
        btn.removeAttribute('disabled');
      }
    });

    if (!isLoading) {
      this.updateToolbarUI();
    }
  }

  showResultModal({ success, title, message, okText, stayText, showStayOption = false }) {
    return new Promise((resolve) => {
      const modal = document.getElementById('gmUploadResultModal');
      const titleEl = document.getElementById('gmResultTitle');
      const subtitleEl = document.getElementById('gmResultSubtitle');
      const messageEl = document.getElementById('gmResultMessage');
      const okBtn = document.getElementById('gmResultOkBtn');
      const stayBtn = document.getElementById('gmResultStayBtn');
      const iconWrap = document.getElementById('gmResultIconWrap');
      const icon = document.getElementById('gmResultIcon');

      if (!modal || !titleEl || !messageEl || !okBtn) {
        resolve({ success: false, action: 'dismiss' });
        return;
      }

      const allowStay = Boolean(success && showStayOption && stayBtn);

      titleEl.textContent = title;
      if (subtitleEl) {
        subtitleEl.textContent = success ? 'Saved successfully' : 'Could not complete';
      }
      messageEl.textContent = message;
      okBtn.textContent = okText || (success ? (allowStay ? 'Back to Main' : 'OK') : 'Try Again');

      if (stayBtn) {
        if (allowStay) {
          stayBtn.textContent = stayText || 'Stay on this page';
          stayBtn.classList.remove('hidden');
        } else {
          stayBtn.classList.add('hidden');
        }
      }

      // Tone drives the icon chip; the button keeps its design-system classes.
      // These lines used to rewrite `class` wholesale, which left a green chip
      // on a failure and reintroduced a shadow and a hover lift on the button.
      if (iconWrap) iconWrap.setAttribute('data-tone', success ? 'positive' : 'negative');
      if (icon) {
        icon.innerHTML = success
          ? '<circle cx="12" cy="12" r="9"/><path d="M8.5 12.5l2.5 2.5 4.5-5"/>'
          : '<path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><path d="M12 9v4M12 17h.01"/>';
      }
      if (okBtn) okBtn.setAttribute('data-variant', success ? 'primary' : 'destructive');

      modal.classList.remove('hidden', 'closing');

      const cleanup = () => {
        okBtn.removeEventListener('click', onOk);
        stayBtn?.removeEventListener('click', onStay);
        modal.removeEventListener('click', onOverlayClick);
      };

      const closeModal = (action) => {
        cleanup();
        modal.classList.add('closing');
        setTimeout(() => {
          modal.classList.add('hidden');
          modal.classList.remove('closing');
          if (stayBtn) stayBtn.classList.add('hidden');
          resolve({ success, action });
        }, 150);
      };

      const onOk = () => closeModal(allowStay ? 'home' : 'stay');
      const onStay = () => closeModal('stay');
      const onOverlayClick = (e) => {
        if (e.target === modal) closeModal(allowStay ? 'stay' : 'stay');
      };

      okBtn.addEventListener('click', onOk);
      if (allowStay) stayBtn.addEventListener('click', onStay);
      modal.addEventListener('click', onOverlayClick);
    });
  }
}
